import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ClientsAdminApi } from '../adminApi.js'
import type { AdminClientSummary, RegistrationContact } from '../index.js'
import { Save } from 'lucide-react'
import { Button } from '../../core/index.js'
import { PhoneInput } from '../../core/index.js'

/** The admin calls this screen uses. */
type AdminApi = ClientsAdminApi

const INPUT_CLASS = 'w-full border border-marine/25 bg-white px-2.5 py-1.5 text-xs text-ink focus:outline-none focus:border-marine'

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
    </div>
  )
}

/**
 * Edits who to reach at a client - name, email and phone number, and nothing
 * else about the registration. Starts from what the list shows (which may be
 * a name borrowed from the matching operator login), so saving also puts
 * that name on the registration itself.
 */
export function ClientContactEditor({ api, client, onDone }: { api: AdminApi; client: AdminClientSummary; onDone: () => void }) {
  const queryClient = useQueryClient()
  const [firstName, setFirstName] = useState(client.contactFirstName ?? '')
  const [lastName, setLastName] = useState(client.contactLastName ?? '')
  const [email, setEmail] = useState(client.contactEmail)
  const [phone, setPhone] = useState(client.contactPhone ?? '')
  const idPrefix = `contact-${client.registrationId}`
  // The editor sits at the foot of the client's panel, under a demo client's list of sessions, so the pencil on
  // the row can open it well below the fold. Bringing it into view is what makes that click visibly do something.
  const formRef = useRef<HTMLFormElement>(null)
  useEffect(() => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [])

  const mutation = useMutation({
    mutationFn: (contact: RegistrationContact) => api.updateContact(client.registrationId, contact),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
      onDone()
    },
  })

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    mutation.mutate({
      contactFirstName: firstName.trim(),
      contactLastName: lastName.trim(),
      contactEmail: email.trim(),
      // An emptied field means "no phone number on file".
      contactPhone: phone.trim() || null,
    })
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="border border-marine/15 bg-white p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="First name" htmlFor={`${idPrefix}-first`}>
          <input id={`${idPrefix}-first`} className={INPUT_CLASS} value={firstName} onChange={(e) => setFirstName(e.target.value)} required autoFocus />
        </Field>
        <Field label="Last name" htmlFor={`${idPrefix}-last`}>
          <input id={`${idPrefix}-last`} className={INPUT_CLASS} value={lastName} onChange={(e) => setLastName(e.target.value)} required />
        </Field>
        <Field label="Email" htmlFor={`${idPrefix}-email`}>
          <input id={`${idPrefix}-email`} type="email" className={INPUT_CLASS} value={email} onChange={(e) => setEmail(e.target.value)} required />
        </Field>
        <Field label="Phone (optional)" htmlFor={`${idPrefix}-phone`}>
          <PhoneInput id={`${idPrefix}-phone`} className={INPUT_CLASS} value={phone} onChange={setPhone} />
        </Field>
      </div>

      <p className="text-[11px] text-ink-mid mt-3">
        Changes the contact on file only. Operator logins keep their current username, even if it is the old contact email.
      </p>
      {mutation.isError && <p className="text-[11px] font-bold text-coral mt-2">{mutation.error.message}</p>}

      <div className="flex items-center gap-3 mt-3">
        <Button type="submit" icon={Save} size="sm" disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Save contact'}
        </Button>
        <button type="button" onClick={onDone} disabled={mutation.isPending} className="text-xs font-bold text-marine disabled:opacity-60">
          Cancel
        </button>
      </div>
    </form>
  )
}
